import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Upload, Pencil, Copy, Trash2 } from 'lucide-react';
import TemplateRenderer from './TemplateRenderer';
import { api } from '../api';

export default function TemplateList({ userId, onEdit }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { templates } = await api.listTemplates(userId);
      setTemplates(templates);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (t) => {
    await api.createTemplate({ name: `${t.name} (copy)`, json: t.json, userId });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    await api.deleteTemplate(t.id);
    load();
  };

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await api.uploadTemplate(file, userId);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <button className="button" onClick={() => onEdit(null)}>
          <Plus size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> New template
        </button>
        <button className="button" style={{ background: '#374151' }} onClick={() => fileInput.current.click()}>
          <Upload size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Upload (.json / .html)
        </button>
        <input ref={fileInput} type="file" accept=".json,.html,.htm" onChange={onUpload} style={{ display: 'none' }} />
      </div>

      {error && <p style={{ color: '#f87171' }}>{error}</p>}
      {loading && <p style={{ color: '#9aa7bd' }}>Loading…</p>}
      {!loading && templates.length === 0 && <p style={{ color: '#9aa7bd' }}>No templates yet. Create or upload one to get started.</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {templates.map((t) => (
          <div key={t.id} style={{ background: '#111827', borderRadius: 10, overflow: 'hidden', border: '1px solid #1f2a3d' }}>
            <TemplateRenderer template={t.json} width={300} />
            <div style={{ padding: 12 }}>
              <div style={{ color: '#e5e7eb', fontWeight: 600, marginBottom: 8 }}>{t.name}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <IconBtn onClick={() => onEdit(t)} title="Edit"><Pencil size={16} /></IconBtn>
                <IconBtn onClick={() => duplicate(t)} title="Duplicate"><Copy size={16} /></IconBtn>
                <IconBtn onClick={() => remove(t)} title="Delete" color="#f87171"><Trash2 size={16} /></IconBtn>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const IconBtn = ({ children, onClick, title, color = '#cbd5e1' }) => (
  <button onClick={onClick} title={title}
    style={{ background: '#1f2a3d', border: 'none', color, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
    {children}
  </button>
);
